// [S-061] Templates data hooks. Backed by the admin REST API
// `/layer1/templates` (list) and `/layer1/templates/:id` (detail) — both
// return only `certified` or `re_certified` L1 templates.
//
// Why REST and not the find_certified_template MCP tool: the MCP tool is
// single-match by design ("find the standard template I need for THIS
// deal"), not a browse list. The REST endpoints sit on the same
// TemplateService and respect identical tenant/layer isolation; they
// just expose multi-record reads.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi, useApiBlob, getApiErrorMessage } from "./api";
import { useSubmitL1Draft } from "./l1TemplatesQuery";

// Mirrors the LifecycleTemplate JSON returned by serializeTemplate
// in apps/admin/src/routes/templates.ts. Kept locally rather than
// imported from @csai/core because the core entity uses Date objects
// and we receive ISO strings on the wire.
/**
 * [S-802 follow-up] Where the author's edit left each variable, sent WITH the
 * content it belongs to.
 *
 * The editor tracks variables as CKEditor markers, which transform through
 * every edit, so on save it can say where each one ended up. The server
 * re-anchors the tag set to those offsets in the same request. A variable the
 * author destroyed is simply absent, and the server drops that one by name
 * instead of invalidating the whole set — which is what happened to EVERY
 * variable, on every edit, before this existed.
 */
export type SubmitDraftVariables = {
  /** Which set the editor drew, and so which one to write back. */
  target: "approved" | "proposal";
  /** The template the variables were drawn from. Differs from the saved id
   *  only on clone-as-new-draft, where the set is carried to the new id. */
  fromTemplateId?: string;
  slots: Array<{ id: string; start: number; end: number }>;
};

/**
 * [S-802 follow-up] What a save did to the template's variables.
 *
 * The server is the one that knows: it re-anchors the set onto the stored
 * content, or — when the content arrived with no way to follow the variables —
 * retires a set that can no longer describe it. Either way the author is the
 * person who needs to hear it, and the only alternative to saying it here is
 * their finding out the next time a draft quietly comes out ungrounded.
 */
export type SaveVariablesOutcome =
  /** The set followed the edit. `dropped` names the variables the author's own
   *  edit destroyed — a list, where it used to be the entire set, silently. */
  | { kept: number; dropped: string[] }
  /** The set could not follow and was cleared. */
  | { retired: string[]; slots: number };

export function describeVariablesOutcome(
  outcome: SaveVariablesOutcome | null | undefined
): string | null {
  if (outcome === null || outcome === undefined) return null;
  if ("retired" in outcome) {
    if (outcome.retired.length === 0) return null;
    const one = outcome.slots === 1;
    return `${outcome.slots} variable${one ? "" : "s"} on this template no longer matched its text and ${one ? "was" : "were"} cleared. Re-run tagging to identify ${one ? "it" : "them"} again.`;
  }
  if (outcome.dropped.length === 0) return null;
  const names = outcome.dropped.join(", ");
  return outcome.dropped.length === 1
    ? `${outcome.kept} variable${outcome.kept === 1 ? "" : "s"} kept. One was removed by this edit: ${names}.`
    : `${outcome.kept} variable${outcome.kept === 1 ? "" : "s"} kept. ${outcome.dropped.length} were removed by this edit: ${names}.`;
}

/** A draft save's response: the template, plus what became of its variables. */
export type SubmitDraftResponse = CertifiedTemplateRow & {
  variables?: SaveVariablesOutcome | null;
};

export type CertifiedTemplateRow = {
  id: string;
  tenantId: string | null;
  layerSource: "L1" | "L2";
  // S-216 — human-readable title (null for legacy rows; UI falls back
  // to the guild/dealType/tier derived label when missing).
  title?: string | null;
  dealType:
    | "feature_film"
    | "tv_series"
    | "streaming"
    | "documentary"
    | "commercial"
    | "short_form";
  guild: "SAG" | "DGA" | "WGA" | "IATSE" | "AFM" | "PGA";
  tier: "basic" | "professional" | "enterprise";
  content: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  lifecycleState:
    | "draft"
    | "certified"
    | "under_review"
    | "re_certified"
    | "deprecated";
  successorId: string | null;
  submittedBy: string;
  // S-216 — anchor CBA picked manually or via auto-tag. Nullable when
  // the user hasn't picked a CBA yet (legacy seeded templates also
  // leave this null until backfill).
  anchorCBA?: string | null;
  // [S-633] Agreement form (deal_memo | long_form). null/absent → untagged.
  // Drives the deal-memo → long-form promotion's template selection.
  documentForm?: "deal_memo" | "long_form" | null;
  // [S-221] Peer-review handoff metadata. Both nullable: a template
  // without an assigned reviewer leaves these as null.
  peerReviewerUid?: string | null;
  peerReviewedAt?: string | null;
  // [S-720] Set when this L2 template was forked from a marketplace
  // listing (apps/admin/src/routes/templates.ts's serializeTemplate
  // already includes this — just wasn't in this type until S-721 needed
  // the timestamp on the Marketplace card). Absent/null for a template
  // authored from scratch.
  importedFrom?: {
    listingId: string;
    publisherId: string | null;
    importedAt: string;
  } | null;
  // [Review Engine V2] How much of this template's clause text the clause_type
  // taxonomy could name when it was certified. Null/absent = never measured
  // (certified before the check shipped, or the classifier was unreachable) —
  // which is NOT the same as "fully covered", so the UI must not render a score
  // for it.
  taxonomyCoverage?: TaxonomyCoverageRow | null;
};

/** [Review Engine V2] One clause the taxonomy could not confidently name. */
export type ClauseCoverageRow = {
  index: number;
  heading: string;
  span: { start: number; end: number };
  clauseType: string;
  confidence: number;
  rationale: string;
};

export type TaxonomyCoverageRow = {
  totalClauses: number;
  typedClauses: number;
  /** Genuinely unnameable — a MISSING clause_type. */
  uncovered: ClauseCoverageRow[];
  /** Named, but weakly — the catalog entry's spec doesn't separate it from a
   *  confusable sibling. A different defect with a different fix, so it is
   *  rendered separately, never summed into one "problems" count. */
  lowConfidence: ClauseCoverageRow[];
  /** A `paragraph` method at low confidence means the template didn't parse into
   *  clauses at all — every "uncovered" clause under it is suspect, so the UI
   *  says so rather than reporting a taxonomy gap that may not exist. */
  segmentation: { method: "structural" | "paragraph"; confidence: number };
  catalogScope: "platform" | { tenantId: string };
  checkedAt: string;
};

export type CertifiedLibraryFilter = {
  dealType?: CertifiedTemplateRow["dealType"];
  guild?: CertifiedTemplateRow["guild"];
  tier?: CertifiedTemplateRow["tier"];
  // [S-633 follow-up] deal_memo (short form) | long_form.
  documentForm?: "deal_memo" | "long_form";
};

export const TEMPLATES_LIST_KEY = ["templates", "L1", "list"] as const;
export const TEMPLATE_DETAIL_KEY = ["templates", "L1", "detail"] as const;

function toQueryString(filter: CertifiedLibraryFilter): string {
  const params = new URLSearchParams();
  if (filter.dealType !== undefined) params.set("dealType", filter.dealType);
  if (filter.guild !== undefined) params.set("guild", filter.guild);
  if (filter.tier !== undefined) params.set("tier", filter.tier);
  if (filter.documentForm !== undefined) params.set("documentForm", filter.documentForm);
  const s = params.toString();
  return s.length === 0 ? "" : `?${s}`;
}

/**
 * Lists L1 certified templates. The server filters by lifecycleState
 * (`certified` | `re_certified` only) — drafts and deprecated never
 * appear here.
 */
export function useCertifiedTemplates(
  filter: CertifiedLibraryFilter
): UseQueryResult<CertifiedTemplateRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...TEMPLATES_LIST_KEY, filter] as const,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api<{ templates: CertifiedTemplateRow[] }>(
        `/layer1/templates${toQueryString(filter)}`
      );
      // Defense-in-depth: assert the server honoured the L1 + lifecycle
      // contract. A regression here would silently surface drafts or L2
      // templates to attorneys browsing the certified templates.
      const violations = res.templates.filter(
        (t) =>
          t.layerSource !== "L1" ||
          (t.lifecycleState !== "certified" &&
            t.lifecycleState !== "re_certified")
      );
      if (violations.length > 0) {
        throw new Error(
          `Layer/lifecycle invariant violated on ${violations.length} row(s) — refusing to render`
        );
      }
      return res.templates;
    },
  });
}

/**
 * Lists the caller's own tenant L2 certified templates. Returns []
 * when the caller has no tenant (platform_admin with studioId=null)
 * or studioId is unavailable — callers can splice the result into
 * their L1 list unconditionally.
 *
 * Server-side: `GET /tenants/:tid/templates` filters to certified+
 * re_certified via `findApplicable`. Drafts and deprecated states
 * land via the future admin-only `lifecycle=all` query param (TBD).
 */
export function useTenantCertifiedTemplates(
  tenantId: string,
  filter: CertifiedLibraryFilter
): UseQueryResult<CertifiedTemplateRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["templates", "L2", "list", tenantId, filter] as const,
    enabled: tenantId.length > 0,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api<{ templates: CertifiedTemplateRow[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/templates${toQueryString(filter)}`
      );
      // Same invariant check — L2 templates for the requested tenant,
      // certified or re_certified.
      const violations = res.templates.filter(
        (t) =>
          t.layerSource !== "L2" ||
          (t.lifecycleState !== "certified" &&
            t.lifecycleState !== "re_certified")
      );
      if (violations.length > 0) {
        throw new Error(
          `Layer/lifecycle invariant violated on ${violations.length} L2 row(s) — refusing to render`
        );
      }
      return res.templates;
    },
  });
}

// ────────────────────────────────────────────────────────────────────────
// Admin lifecycle management hooks
// ────────────────────────────────────────────────────────────────────────

/**
 * Admin list — fetches L1 + (optional) tenant L2 with `?lifecycle=all`
 * so drafts, under-review, and deprecated templates surface. Server
 * silently downgrades to certified-only when the caller isn't an admin,
 * so this hook is safe to mount unconditionally; results just won't
 * include drafts for non-admins.
 *
 * Returns L1 + L2 combined, deduped, L2 first.
 */
export function useAdminTemplates(
  tenantId: string,
  filter: CertifiedLibraryFilter
): UseQueryResult<CertifiedTemplateRow[], Error> {
  const api = useApi("admin");
  const params = toQueryString({ ...filter });
  // `lifecycle=all` ride-alongs on the existing filter query string.
  const sep = params.length === 0 ? "?" : "&";
  return useQuery({
    queryKey: ["templates", "admin", "list", tenantId, filter] as const,
    // keep prior rows visible while a filter/scope change refetches (no blank flash)
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const l1 = await api<{ templates: CertifiedTemplateRow[] }>(
        `/layer1/templates${params}${sep}lifecycle=all`
      );
      const l2 =
        tenantId.length > 0
          ? await api<{ templates: CertifiedTemplateRow[] }>(
              `/tenants/${encodeURIComponent(tenantId)}/templates${params}${sep}lifecycle=all`
            )
          : { templates: [] as CertifiedTemplateRow[] };
      const seen = new Set<string>();
      const out: CertifiedTemplateRow[] = [];
      for (const t of [...l2.templates, ...l1.templates]) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        out.push(t);
      }
      return out;
    },
  });
}

/**
 * Clones a certified / re_certified / under_review / deprecated
 * template into a fresh draft and returns the new template id so the
 * caller can navigate the user into the editor. Reuses the same
 * submit-draft mutations the single-file authoring flow uses; the
 * server enforces the role gate (platform_admin for L1,
 * tenant_admin-of-tenant for L2).
 *
 * Returning a flat `{ clone, isPending, error }` shape rather than a
 * full UseMutationResult keeps the caller's code short — TanStack
 * Query's lifecycle isn't useful here because we always want to
 * navigate on success.
 */
export type CloneAsDraftResult = {
  clone: (
    template: CertifiedTemplateRow,
    /** `variables` is what the save did to them — the caller navigates away
     *  immediately, so it has to carry the news with it or the author never
     *  hears which variable their edit removed. */
    onSuccess: (newTemplateId: string, variables?: SaveVariablesOutcome | null) => void,
    overrides?: { content?: string; variables?: SubmitDraftVariables }
  ) => void;
  isPending: boolean;
  error: string | null;
};

export function useCloneTemplateAsDraft(): CloneAsDraftResult {
  // The L2 mutation needs the tenantId at hook-construction time, so
  // we instantiate it lazily-per-template by reading `tenantId` from
  // the template passed into `clone`. The L1 hook is tenant-less.
  const submitL1 = useSubmitL1Draft();
  // We don't know the tenantId at hook-time; the L2 hook needs one,
  // so use empty string and rely on `clone()` rebinding via a fresh
  // hook. Easier path: do a useMutation locally so we don't need to
  // know tenantId statically.
  const api = useApi("admin");
  const qc = useQueryClient();
  const localL2 = useMutation({
    mutationFn: async ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: {
        id: string;
        title: string | null;
        dealType: CertifiedTemplateRow["dealType"];
        guild: CertifiedTemplateRow["guild"];
        tier: CertifiedTemplateRow["tier"];
        anchorCBA: string | null;
        content: string;
        effectiveFrom: string;
        variables?: SubmitDraftVariables;
      };
    }) =>
      api<SubmitDraftResponse>(
        `/tenants/${encodeURIComponent(tenantId)}/templates/draft`,
        { method: "POST", body }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const clone = (
    template: CertifiedTemplateRow,
    onSuccess: (newTemplateId: string, variables?: SaveVariablesOutcome | null) => void,
    overrides?: { content?: string; variables?: SubmitDraftVariables }
  ): void => {
    const newId = `tpl-clone-${Date.now().toString(36)}-${Math.floor(
      Math.random() * 0xffffff
    )
      .toString(16)
      .padStart(6, "0")}`;
    const body = {
      id: newId,
      // S-216 — carry the source template's title + anchor CBA into the
      // clone. Dropping them stripped the human title and forced the
      // author to re-pick a CBA before the certify gate would pass.
      title: template.title ?? null,
      dealType: template.dealType,
      guild: template.guild,
      tier: template.tier,
      anchorCBA: template.anchorCBA ?? null,
      content: overrides?.content ?? template.content,
      effectiveFrom: new Date().toISOString(),
      // [S-802 follow-up] A clone used to start with NO variables — the source
      // template's reviewed set simply did not come along. Carried here,
      // re-anchored onto the author's edit; the server lands it as this
      // author's proposal, never as an approved fact about a template nobody
      // has reviewed.
      ...(overrides?.variables === undefined ? {} : { variables: overrides.variables }),
    };
    if (template.layerSource === "L1") {
      submitL1.mutate(body, { onSuccess: (saved) => onSuccess(newId, saved.variables) });
    } else if (template.tenantId !== null) {
      localL2.mutate(
        { tenantId: template.tenantId, body },
        { onSuccess: (saved) => onSuccess(newId, saved.variables) }
      );
    }
  };

  const err = submitL1.error ?? localL2.error;
  return {
    clone,
    isPending: submitL1.isPending || localL2.isPending,
    error: err === null ? null : getApiErrorMessage(err) ?? err.message,
  };
}

export type FlagForReviewInput = {
  templateId: string;
  tenantId: string | null; // null for L1
  reason: string;
};

export function useFlagForReview(): UseMutationResult<
  void,
  Error,
  FlagForReviewInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, tenantId, reason }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/flag-review`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/flag-review`;
      return api<void>(url, { method: "POST", body: { reason } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// [S-221] Peer-review handoff mutations. Mirrors the flag/deprecate
// pattern: caller passes templateId + tenantId (null for L1); the
// hook routes to the right URL. On success we invalidate the
// templates query family so AttestationCard's "Peer review" row + the
// underlying admin lookup pick up the new state.
export type AssignPeerReviewerInput = {
  templateId: string;
  tenantId: string | null;
  peerReviewerUid: string;
};

export function useAssignPeerReviewer(): UseMutationResult<
  CertifiedTemplateRow,
  Error,
  AssignPeerReviewerInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, tenantId, peerReviewerUid }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/peer-review/assign`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/peer-review/assign`;
      return api<CertifiedTemplateRow>(url, {
        method: "POST",
        body: { peerReviewerUid },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export type RecordPeerReviewInput = {
  templateId: string;
  tenantId: string | null;
};

export function useRecordPeerReview(): UseMutationResult<
  CertifiedTemplateRow,
  Error,
  RecordPeerReviewInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, tenantId }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/peer-review/record`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/peer-review/record`;
      return api<CertifiedTemplateRow>(url, { method: "POST" });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// [S-214 retired] The per-template clause index is gone. Nothing ever
// produced clause records, and clause-level knowledge belongs to the
// precedent engine (clause-indexed by design) — a certified template is
// certified as a whole document, and anything needing clause granularity
// over its body computes it on demand via ClauseExtractionPort (S-509f).

// [S-224] Cascade-preview counts for the Deprecate dialog. Returns 0
// for either field when the server-side query failed or the adapter
// doesn't expose countByTemplateId.
export type TemplateDependents = {
  templateId: string;
  openReviews: number;
  openBriefs: number;
};

export function useTemplateDependents(
  templateId: string | undefined,
  layer: "L1" | "L2",
  tenantId: string = ""
): UseQueryResult<TemplateDependents, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["templates", "dependents", layer, tenantId, templateId] as const,
    enabled:
      templateId !== undefined &&
      templateId.length > 0 &&
      (layer === "L1" || tenantId.length > 0),
    queryFn: async () => {
      const url =
        layer === "L1"
          ? `/layer1/templates/${encodeURIComponent(templateId!)}/dependents`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId!)}/dependents`;
      return api<TemplateDependents>(url);
    },
    // Cascade preview is informational — a failed call shouldn't block
    // the deprecate dialog from opening. The modal renders an
    // "Unknown" state when the query errors.
    retry: false,
  });
}

// [S-222] Per-template audit trail row. Mirrors ActivityRow on the wire.
export type TemplateAuditRow = {
  id: string;
  occurredAt: string;
  toolName: string;
  layerSource: "L1" | "L2" | "both";
  title: string;
  meta: string;
  actor: { uid: string; displayName: string };
};

/**
 * [S-222] Fetches the per-template audit trail (every draft submission,
 * certify, flag-for-review, deprecate event for this template id).
 * Empty page → adapter doesn't implement findByTemplateId or no events
 * fall in the lookback window.
 *
 * NO CALLER TODAY — deliberately retained, not dead code. The panel that
 * used it was removed from TemplateDetail on 2026-07-25: per-template is
 * the wrong home for auditing, and a proper surface will be sited later.
 * The backend behind this (GET /layer1|tenants/:t/templates/:id/audit →
 * ActivityFeedService.getTemplateAuditTrail → Cloud Logging) is fully
 * wired and now records callerUid, so re-surfacing it is a render away.
 */
export function useTemplateAuditTrail(
  templateId: string | undefined,
  layer: "L1" | "L2",
  tenantId: string = ""
): UseQueryResult<TemplateAuditRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["templates", "audit", layer, tenantId, templateId] as const,
    enabled:
      templateId !== undefined &&
      templateId.length > 0 &&
      (layer === "L1" || tenantId.length > 0),
    queryFn: async () => {
      const url =
        layer === "L1"
          ? `/layer1/templates/${encodeURIComponent(templateId!)}/audit`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId!)}/audit`;
      const res = await api<{ rows: TemplateAuditRow[]; nextCursor: string | null }>(url);
      return res.rows;
    },
    // Tolerate per-tenant lookup against L1 templates (the audit log
    // strictly tags everything under the `layer1` namespace; tenant_*
    // callers shouldn't see L1 audit anyway via the L2 route).
    retry: false,
  });
}

/**
 * [S-213] Triggers a DOCX download for a template. Hits the admin
 * export endpoint, then synthesizes an <a download> click to save the
 * blob locally. Returns the filename so callers can surface a toast.
 */
export type ExportTemplateInput = {
  templateId: string;
  tenantId: string | null; // null for L1
};

export function useExportTemplate(): UseMutationResult<
  { filename: string },
  Error,
  ExportTemplateInput
> {
  const blobApi = useApiBlob("admin");
  return useMutation({
    mutationFn: async ({ templateId, tenantId }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/export`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/export`;
      const { blob, filename } = await blobApi(url, { method: "POST" });
      const finalName = filename ?? `${templateId}.docx`;
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // The browser keeps a reference until after the navigation, so
        // a small delay is safer than revoking inline.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      return { filename: finalName };
    },
  });
}

// S-216 — retroactively retag any existing template. tenantId === null
// hits the L1 path; non-null hits the L2 path. All fields optional;
// passing nothing returns a 400 (server-side refine).
export type UpdateTemplateTagsInput = {
  templateId: string;
  tenantId: string | null;
  // null clears the title; string sets it; omitted preserves.
  title?: string | null;
  dealType?: CertifiedTemplateRow["dealType"];
  guild?: CertifiedTemplateRow["guild"];
  tier?: CertifiedTemplateRow["tier"];
  // null clears the anchor CBA; string sets it; omitted preserves.
  anchorCBA?: string | null;
  // [S-633] null clears the agreement form; a value tags it; omitted preserves.
  documentForm?: "deal_memo" | "long_form" | null;
};

export function useUpdateTemplateTags(): UseMutationResult<
  CertifiedTemplateRow,
  Error,
  UpdateTemplateTagsInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, tenantId, ...patch }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/tags`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/tags`;
      return api<CertifiedTemplateRow>(url, {
        method: "PATCH",
        body: patch,
      });
    },
    onSuccess: () => {
      // Invalidate the broader template cache so both the certified
      // library list and the detail page pick up the new tags.
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export type DeprecateInput = {
  templateId: string;
  tenantId: string | null; // null for L1
  /** Server expects `asOf` — effective-from of the deprecation. */
  asOf: string; // ISO datetime
  successorId?: string | null;
};

export function useDeprecate(): UseMutationResult<void, Error, DeprecateInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, tenantId, asOf, successorId }) => {
      const url =
        tenantId === null
          ? `/layer1/templates/${encodeURIComponent(templateId)}/deprecate`
          : `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId)}/deprecate`;
      return api<void>(url, {
        method: "POST",
        body: { asOf, successorId: successorId ?? null },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

// [S-718] Per-tenant L1 template exclusion (S-063b backend, previously with
// no web UI to drive it). Only meaningful for L1 templates — a tenant never
// excludes its own L2 templates.
export type TemplateExclusion = {
  tenantId: string;
  templateId: string;
  excludedAt: string;
  excludedBy: { uid: string; role: string };
  reason: string | null;
};

export function useTemplateExclusions(
  tenantId: string
): UseQueryResult<TemplateExclusion[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["templates", "exclusions", tenantId] as const,
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const res = await api<{ tenantId: string; exclusions: TemplateExclusion[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/template-exclusions`
      );
      return res.exclusions;
    },
  });
}

export type ExcludeTemplateInput = {
  tenantId: string;
  templateId: string;
  reason?: string | null;
};

export function useExcludeTemplate(): UseMutationResult<
  TemplateExclusion,
  Error,
  ExcludeTemplateInput
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, templateId, reason }) =>
      api<TemplateExclusion>(
        `/tenants/${encodeURIComponent(tenantId)}/template-exclusions/${encodeURIComponent(templateId)}`,
        { method: "POST", body: { reason: reason ?? null } }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: ["templates", "exclusions", tenantId] });
    },
  });
}

export type IncludeTemplateInput = { tenantId: string; templateId: string };

export function useIncludeTemplate(): UseMutationResult<void, Error, IncludeTemplateInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, templateId }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/template-exclusions/${encodeURIComponent(templateId)}`,
        { method: "DELETE" }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: ["templates", "exclusions", tenantId] });
    },
  });
}

/**
 * Single template by id, including all lifecycle states (admin scope).
 * The default useTemplateDetail rejects everything outside
 * `certified` / `re_certified` because end-users should never see a
 * draft. The edit flow (#251) needs to read a draft to prefill the
 * editor, so this admin variant accepts every state.
 *
 * [S-721 follow-up] Tries the caller's OWN L2 first when a tenantId is
 * known, falling back to L1; L1-only when no tenant is in scope
 * (platform_admin). Was L1-first unconditionally, which silently broke
 * every caller of this hook for a Marketplace-imported id: import()
 * deliberately reuses the source listing's id for the tenant's own L2
 * draft (see MarketplaceService.import), so an L1-first lookup ALWAYS
 * found the platform's certified original instead — a tenant clicking
 * "Edit" on their own imported draft landed on the read-only L1 detail
 * page instead of their draft editor, with no way to ever reach it
 * through this route. A tenant-scoped caller asking for a template by id
 * is asking about their own content; L1 stays the fallback for the
 * (legitimate, common) case where no L2 draft exists at that id at all.
 */
export function useAdminTemplate(
  templateId: string | undefined,
  tenantId: string = ""
): UseQueryResult<CertifiedTemplateRow, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["templates", "admin", "detail", templateId, tenantId] as const,
    enabled: templateId !== undefined && templateId.length > 0,
    queryFn: async () => {
      if (tenantId.length > 0) {
        try {
          const t = await api<CertifiedTemplateRow>(
            `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId!)}?lifecycle=all`
          );
          return t;
        } catch {
          // Fall back to L1 — its error (not the L2 attempt's) is what
          // should surface if neither exists, matching this hook's
          // long-standing "the last attempt's error wins" convention.
          const t = await api<CertifiedTemplateRow>(
            `/layer1/templates/${encodeURIComponent(templateId!)}?lifecycle=all`
          );
          return t;
        }
      }
      // No tenant in scope (platform_admin) — L1 only, as before.
      const t = await api<CertifiedTemplateRow>(
        `/layer1/templates/${encodeURIComponent(templateId!)}?lifecycle=all`
      );
      return t;
    },
  });
}

/**
 * Single template by id. Tries the caller's own L2 first when a
 * tenantId is known, falling back to L1; L1-only when no tenant is in
 * scope (platform_admin). Detail page surfaces a not-found state if
 * both attempts fail.
 *
 * Why this shape: /library/:templateId doesn't carry layer info in
 * the URL. Resolving the layer transparently here keeps the route
 * shape simple and lets the same detail screen render either layer.
 * Pass `tenantId` (derived from session.studioId) so the L2 attempt
 * has the right scope; falls back gracefully when tenantId is empty
 * (platform_admin with studioId=null).
 *
 * [S-721 follow-up] Was L1-first unconditionally — the same collision
 * useAdminTemplate had (see its doc comment): a Marketplace-imported id
 * always has a genuinely certified L1 original at that exact id, so an
 * L1-first lookup ALWAYS won once the tenant certified their own L2
 * copy too, and this detail page (reached right after certifying, and
 * from Template Management's "Open") kept showing the platform's
 * original instead of the tenant's own newly-certified template.
 */
export function useTemplateDetail(
  templateId: string | undefined,
  tenantId: string = ""
): UseQueryResult<CertifiedTemplateRow, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...TEMPLATE_DETAIL_KEY, templateId, tenantId] as const,
    enabled: templateId !== undefined && templateId.length > 0,
    queryFn: async () => {
      const fetchL1 = async (): Promise<CertifiedTemplateRow> => {
        const t = await api<CertifiedTemplateRow>(
          `/layer1/templates/${encodeURIComponent(templateId!)}`
        );
        if (
          t.layerSource !== "L1" ||
          (t.lifecycleState !== "certified" &&
            t.lifecycleState !== "re_certified")
        ) {
          throw new Error(
            `Layer/lifecycle invariant violated on template '${t.id}' — refusing to render`
          );
        }
        return t;
      };
      const fetchL2 = async (): Promise<CertifiedTemplateRow> => {
        const t = await api<CertifiedTemplateRow>(
          `/tenants/${encodeURIComponent(tenantId)}/templates/${encodeURIComponent(templateId!)}`
        );
        if (
          t.layerSource !== "L2" ||
          (t.lifecycleState !== "certified" &&
            t.lifecycleState !== "re_certified")
        ) {
          throw new Error(
            `Layer/lifecycle invariant violated on L2 template '${t.id}' — refusing to render`
          );
        }
        return t;
      };
      if (tenantId.length > 0) {
        try {
          return await fetchL2();
        } catch {
          return await fetchL1();
        }
      }
      return await fetchL1();
    },
  });
}

/**
 * [draft permalink] Resolve a template for a /library/:templateId route.
 *
 * `useTemplateDetail` asks WITHOUT `?lifecycle=all`, so the API 404s a draft
 * and every screen built on it claimed the template does not exist — for a
 * template sitting in the tenant's own library. Anyone who left the editor
 * without certifying (session lapsed mid-edit, back button, a bookmarked link)
 * lost the way back to their own draft. Callers who may work on drafts re-ask
 * with `lifecycle=all` (the same request the /edit screen makes).
 *
 * This lives here, not in a screen, because two routes resolve the same
 * permalink (`/library/:id` and `/library/:id/variables`) and a fix in one is
 * a fix the other silently misses. `includeAllStates` is the caller's
 * capability answer — the server re-checks either way.
 */
export function useTemplatePermalink(
  templateId: string | undefined,
  tenantId: string,
  includeAllStates: boolean
): {
  data: CertifiedTemplateRow | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const certifiedOnly = useTemplateDetail(templateId, tenantId);
  // One flag drives both the second request and the states below it, so the
  // three can't disagree — reading a DISABLED query reports neither loading
  // nor error, which renders a screen with nothing on it.
  const askBroadly = includeAllStates && certifiedOnly.isError;
  const anyState = useAdminTemplate(askBroadly ? templateId : undefined, tenantId);
  return {
    data: certifiedOnly.data ?? anyState.data,
    isLoading:
      certifiedOnly.isLoading ||
      (askBroadly && anyState.data === undefined && !anyState.isError),
    isError: certifiedOnly.isError && (!askBroadly || anyState.isError),
    // The broader attempt's failure is the one that describes the outcome.
    error: anyState.error ?? certifiedOnly.error,
  };
}
